<script setup lang="ts">
import type {Organization} from '~/types/api';
type AppError = {
  statusCode?: number;
  statusMessage?: string;
  message?: string;
};

const props = defineProps<{
  error: AppError;
}>();

const org = useState<Organization>('org');
const showAppLayout = computed(() => Boolean(org.value));

const isNotFound = computed(() => props.error?.statusCode === 404);
const errorTitle = computed(() =>
  isNotFound.value ? 'Spooky!' : String(props.error?.statusCode ?? 'Error')
);
const errorMessage = computed(() => {
  if (isNotFound.value) {
    return "Ghosty couldn't seem to find what you were looking for!";
  }
  return props.error?.statusMessage || props.error?.message || 'Something went wrong.';
});

useSeoMeta({
  title: 'Page not found',
  description: 'We are sorry but this page could not be found.',
});

useHead({
  htmlAttrs: {
    lang: 'en',
  },
});
</script>

<template>
  <NuxtLayout v-if="showAppLayout" name="app">
    <UMain>
      <UContainer class="min-h-[calc(100vh-2rem)] flex items-center justify-center py-6">
        <div class="mx-auto flex max-w-xl flex-col items-center gap-4 text-center">
          <img
            src="/img/404-ghost.png"
            alt="Ghost detective"
            class="h-40 w-auto floating-ghost"
          />
          <p class="text-4xl font-semibold">{{ errorTitle }}</p>
          <p class="text-lg">{{ errorMessage }}</p>
          <p class="text-sm text-gray-500">Aren't that legs.</p>
          <button
            type="button"
            class="rounded-md bg-primary-500 px-4 py-2 text-white hover:bg-primary-600"
            @click="clearError({redirect: '/'})"
          >
            Go home
          </button>
        </div>
      </UContainer>
    </UMain>
  </NuxtLayout>
  <UMain v-else>
    <UContainer class="min-h-[calc(100vh-2rem)] flex items-center justify-center py-6">
      <div class="mx-auto flex max-w-xl flex-col items-center gap-4 text-center">
        <img src="/img/404-ghost.png" alt="Ghost detective" class="h-40 w-auto floating-ghost" />
        <p class="text-4xl font-semibold">{{ errorTitle }}</p>
        <p class="text-lg">{{ errorMessage }}</p>
        <button
          type="button"
          class="rounded-md bg-primary-500 px-4 py-2 text-white hover:bg-primary-600"
          @click="clearError({redirect: '/'})"
        >
          Go home
        </button>
      </div>
    </UContainer>
  </UMain>
</template>

<style scoped>
.floating-ghost {
  animation: ghost-float 4s ease-in-out infinite;
}

@keyframes ghost-float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .floating-ghost {
    animation: none;
  }
}
</style>
